import type { ComponentType, ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/** Only allow same-origin relative paths (prevents open redirects). */
export function safeInternalReturnPath(from: unknown): string | null {
  if (typeof from !== "string" || !from.trim()) return null;
  const path = from.trim();
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  return path;
}

type RequireMemberAuthProps = {
  children: ReactNode;
};

/**
 * Route guard for member-only pages (`/member/*`, invite friends, etc.).
 * Unauthenticated users are sent to `/login` with a return URL.
 */
export function RequireMemberAuth({ children }: RequireMemberAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center" role="status">
        <Loader2 className="animate-spin text-[#ff3366]" size={32} aria-hidden />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate to="/login" replace state={{ from: returnTo, signIn: true }} />
    );
  }

  if (user?.accountType === "venue") {
    return <Navigate to="/steps/venue-information" replace />;
  }

  if (user?.accountType !== "member") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Wrap a page component for use in `routes.ts`. */
export function withMemberAuth<P extends object>(Component: ComponentType<P>) {
  function MemberAuthenticatedRoute(props: P) {
    return (
      <RequireMemberAuth>
        <Component {...props} />
      </RequireMemberAuth>
    );
  }
  MemberAuthenticatedRoute.displayName = `MemberAuth(${Component.displayName ?? Component.name ?? "Component"})`;
  return MemberAuthenticatedRoute;
}
